<template>
  <div class="summary-page">
    <div v-if="isHyroxSummary" class="hyrox-summary">
      <div class="hyrox-summary-bg"></div>
      <v-container>
        <div class="hyrox-hero text-center">
          <h1 class="hyrox-title">RACE FINISHED</h1>
          <div class="total-time-display">{{ durationString }}</div>
          <p class="hyrox-subtitle">{{ activeSplit.title }}</p>
        </div>

        <h2 class="font-weight-bold headline my-4 white--text">Segment Analysis</h2>

        <v-list dark color="transparent" class="segment-list">
          <template v-for="(segment, index) in hyroxSegments">
            <v-list-item :key="index" class="segment-item" @click="toggleSegment(index)">
              <v-list-item-avatar tile size="50" class="mr-4 rounded-lg">
                <v-img :src="`/img/uebungen/${segment.icon.replace(/\.[^/.]+$/, '')}.webp`"></v-img>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="segment-title">{{ segment.title }}</v-list-item-title>
              </v-list-item-content>

              <v-list-item-action class="text-right">
                <div class="segment-time">{{ segment.time }}</div>
                <div v-if="segment.comparison" class="segment-comparison" :class="segment.comparisonClass">
                  {{ segment.comparison }}
                </div>
              </v-list-item-action>
              <v-list-item-action class="ml-4">
                <v-icon :class="{ 'rotate-icon': expandedSegmentIndex === index }">mdi-chevron-down</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-expand-transition :key="`expand-${index}`">
              <div v-if="expandedSegmentIndex === index" class="segment-history-wrapper">
                <v-simple-table dense class="segment-history-table">
                  <thead>
                    <tr>
                      <th class="text-left">Datum</th>
                      <th class="text-right">Zeit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, i) in getSegmentHistory(segment)" :key="i">
                      <td>{{ item.date }}</td>
                      <td class="text-right">{{ item.time }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-expand-transition>
          </template>
        </v-list>
      </v-container>
    </div>
    <template v-else>
      <v-container>
        <v-btn style="z-index: 1000" absolute top left color="white" large icon @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-fade-transition appear>
          <div>
            <v-row>
              <v-col cols="12" class="text-center hero-section pulsing-hero" :style="heroStyle">
                <v-img :src="splitImage" :aspect-ratio="1" width="120" class="elevation-6 rounded-lg mx-auto mb-4">
                  <template v-slot:placeholder>
                    <v-skeleton-loader type="image" class="fill-height"></v-skeleton-loader>
                  </template>
                </v-img>
                <h3 class="font-weight-bold display-1 white--text hero-title">SPLIT BEZWUNGEN!</h3>
                <p class="headline-6 font-weight-medium" :style="{ color: splitColor }">{{ splitTitle }}</p>
                <div class="mt-2">
                  <v-chip v-for="tag in focusTags" :key="tag" small dark color="rgba(255, 255, 255, 0.1)" class="ma-1">
                    {{ tag }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-8">
              <v-col class="text-center">
                <p class="motivational-quote">{{ personalizedMessage }}</p>
              </v-col>
            </v-row>

            <v-row v-if="newPersonalRecords.length > 0" class="mt-2">
              <v-col cols="12">
                <h4 class="font-weight-bold headline mb-3 white--text">🏆 Neue Rekorde</h4>
                <v-slide-group dark>
                  <v-slide-item v-for="(pr, index) in newPersonalRecords" :key="`pr-${index}`">
                    <v-card class="summary-card pr-card ma-2">
                      <div class="summary-card-title">{{ pr.exerciseTitle }}</div>
                      <div class="summary-card-type">{{ pr.type }}</div>
                      <div class="summary-card-values">
                        <span class="old-value">{{ pr.oldValue }}</span>
                        <span class="mx-2">→</span>
                        <span class="new-value">{{ pr.newValue }}</span>
                      </div>
                    </v-card>
                  </v-slide-item>
                </v-slide-group>
              </v-col>
            </v-row>

            <v-row v-if="planUpdates.length > 0" class="mt-6">
              <v-col cols="12">
                <h4 class="font-weight-bold headline mb-3 white--text">💡 Plan-Anpassungen</h4>
                <v-slide-group dark>
                  <v-slide-item v-for="(adj, index) in planUpdates" :key="`adj-${index}`">
                    <v-card class="summary-card adj-card ma-2">
                      <div class="summary-card-title">{{ adj.exerciseTitle }}</div>
                      <div class="summary-card-type">Nächstes Startgewicht</div>
                      <div class="summary-card-values">
                        <span class="old-value">{{ adj.oldValue }}</span>
                        <span class="mx-2">→</span>
                        <span class="new-value" :class="adj.isIncrease ? 'increase' : 'decrease'">{{ adj.newValue }}</span>
                      </div>
                    </v-card>
                  </v-slide-item>
                </v-slide-group>
              </v-col>
            </v-row>

            <v-row class="mt-8">
              <v-col cols="12">
                <h4 class="font-weight-bold headline mb-3 white--text">📊 Deine Session</h4>
              </v-col>
              <v-col cols="6" class="pr-2">
                <v-card class="stat-card pa-4 text-center" :style="cardStyle">
                  <div class="stat-emoji">⏱️</div>
                  <div class="stat-value">{{ durationString }}</div>
                  <div class="stat-label">Dauer</div>
                </v-card>
              </v-col>
              <v-col cols="6" class="pl-2">
                <v-card class="stat-card pa-4 text-center" :style="cardStyle">
                  <div class="stat-emoji">🔥</div>
                  <div class="stat-value">{{ animatedCalories.toFixed(0) }}</div>
                  <div class="stat-label">Kalorien</div>
                </v-card>
              </v-col>
              <v-col cols="12" class="pt-4">
                <v-card class="stat-card pa-4 text-center" :style="cardStyle">
                  <div class="stat-emoji">🏃‍♂️</div>
                  <div class="stat-value">{{ animatedDistance.toFixed(0) }} m</div>
                  <div class="stat-label">Gesamtdistanz</div>
                </v-card>
              </v-col>
              <v-col cols="12" class="pt-4">
                <v-card class="stat-card pa-4 text-center" :style="cardStyle">
                  <div class="stat-emoji">🏋️‍♂️</div>
                  <div class="stat-value">{{ animatedVolume.toFixed(0) }} kg</div>
                  <div class="stat-label">Gesamtvolumen</div>
                </v-card>
              </v-col>
              <v-col cols="6" class="pr-2 pt-4">
                <v-card class="stat-card pa-4 text-center" :style="cardStyle">
                  <div class="stat-emoji">📊</div>
                  <div class="stat-value">{{ animatedSets }}</div>
                  <div class="stat-label">Sätze</div>
                </v-card>
              </v-col>
              <v-col cols="6" class="pl-2 pt-4">
                <v-card class="stat-card pa-4 text-center" :style="cardStyle">
                  <div class="stat-emoji">🔄</div>
                  <div class="stat-value">{{ animatedReps }}</div>
                  <div class="stat-label">Wiederholungen</div>
                </v-card>
              </v-col>
            </v-row>

            <v-row class="mt-8">
              <v-col cols="12">
                <h4 class="font-weight-bold headline mb-3 white--text">💡 Hilf uns, besser zu werden!</h4>
                <v-textarea
                  v-model="trainingFeedback"
                  class="feedback-textarea"
                  placeholder="Was können wir an der App verbessern? Dein Feedback geht direkt an uns Entwickler (optional)."
                  rows="4"
                  no-resize
                  filled
                  dark
                  rounded="lg"></v-textarea>
              </v-col>
            </v-row>
          </div>
        </v-fade-transition>
      </v-container>
    </template>
    <v-footer fixed class="pa-4 footer-bg">
      <v-btn block x-large color="primary" @click="saveSessionAndExit" :loading="isSaving" :disabled="saveChangesDialogVisible || isSaving">
        Speichern & Beenden
      </v-btn>
    </v-footer>
    <v-dialog v-model="showSaveFreeTrainingDialog" persistent max-width="450px" dark>
      <v-card class="rounded-xl pa-3 glass-card-dialog">
        <v-card-title class="font-weight-bold justify-center pb-2">
          <v-icon left color="primary">mdi-content-save-outline</v-icon>
          Training speichern?
        </v-card-title>
        <v-card-text class="white--text text-center mt-4">
          Möchtest du dieses freie Training als neuen, wiederverwendbaren Plan in deinem Hub speichern?
        </v-card-text>
        <v-card-actions class="pa-4 pt-2 d-column">
          <v-btn color="primary" depressed rounded large block class="mb-2" @click="handleSaveFreeTraining(true)" :loading="isSaving">
            Ja, als Plan speichern
          </v-btn>
          <v-btn text large block @click="handleSaveFreeTraining(false)" :loading="isSaving"> Nein, nur dieses eine Mal </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <save-changes-dialog
      :dialog.sync="saveChangesDialogVisible"
      :modifications="getWorkoutModifications"
      @save-changes="handleSaveChanges"
      @discard-changes="handleDiscardChanges" />
  </div>
</template>
<script>
import { mapState, mapActions, mapGetters } from "vuex"
import api from "@/api"
import moment from "moment"
import confetti from "canvas-confetti"
import SaveChangesDialog from "./saveChangesDialog.vue"

export default {
  name: "WorkoutSummary",
  components: {
    SaveChangesDialog,
  },
  data() {
    return {
      expandedSegmentIndex: null,
      trainingFeedback: "",
      isSaving: false,
      animatedVolume: 0,
      animatedSets: 0,
      animatedReps: 0,
      unixTimestamp: null,
      animatedCalories: 0,
      animatedDistance: 0, // Hinzugefügt für Distanz-Statistik
      showSaveFreeTrainingDialog: false,
      saveChangesDialogVisible: false,
    }
  },
  computed: {
    ...mapState("runWorkoutStore", [
      "activeSplit",
      "originalActiveSplit",
      "completedSetsMap",
      "workoutStartTime",
      "hubGroupedExercises",
      "activePlanID",
      "hubGroupedSessions",
    ]),
    ...mapGetters("runWorkoutStore", ["getWorkoutModifications"]),
    previousHyroxRace() {
      console.log("oi", this.activeSplit, this.hubGroupedSessions)
      if (!this.activeSplit || !this.hubGroupedSessions) return null

      // Finde alle vergangenen Sessions mit exakt demselben Titel (z.B. "HYROX (Men Pro)")
      const pastRaces = this.hubGroupedSessions.filter(
        (session) => session.execution && session.execution.title === this.activeSplit.title && session.execution.hyroxSegments // Stelle sicher, dass es Segmentdaten gibt
      )

      console.log(pastRaces)
      // Gib die aktuellste dieser vergangenen Sessions zurück (sind bereits nach Datum sortiert)
      return pastRaces.length > 0 ? pastRaces[0] : null
    },
    isHyroxSummary() {
      return this.activeSplit?.isHyrox
    },

    hyroxSegments() {
      if (!this.activeSplit?.isHyrox || !this.completedSetsMap) return []
      console.log("segmenti segmenti ")
      // Hole die Daten des letzten Rennens für den Vergleich
      const previousRaceSegments = this.previousHyroxRace?.execution?.hyroxSegments || []

      return this.activeSplit.exercises
        .map((exercise, index) => {
          const completedSet = (this.completedSetsMap[index] || [])[0]
          if (!completedSet) return null

          const durationInSeconds = Number(completedSet.find((s) => s.text === "Dauer")?.value || 0)

          const formatTime = (seconds) => {
            if (isNaN(seconds) || seconds <= 0) return "---"
            const min = Math.floor(seconds / 60)
            const sec = Math.floor(seconds % 60)
            return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
          }

          const getTitle = () => {
            const weight = completedSet.find((s) => s.text === "Gewicht")?.value
            const reps = completedSet.find((s) => s.text === "Wiederholungen")?.value
            if (weight && reps) return `${exercise.title} (${reps} Reps / ${weight}kg)`
            if (weight) return `${exercise.title} (${weight}kg)`
            return exercise.title
          }
          const getImage = () => {
            return this.getImageForExercise(exercise)
          }

          // --- START: NEUE VERGLEICHSLOGIK ---
          let comparison = null
          let comparisonClass = ""
          const previousSegment = previousRaceSegments.find((seg) => seg.segmentIndex === index)

          if (previousSegment && previousSegment.duration > 0 && durationInSeconds > 0) {
            const diff = durationInSeconds - previousSegment.duration
            const sign = diff <= 0 ? "-" : "+"
            comparisonClass = diff <= 0 ? "faster" : "slower"
            comparison = `${sign}${formatTime(Math.abs(diff))}`
          }
          // --- ENDE: NEUE VERGLEICHSLOGIK ---

          return {
            title: getTitle(),
            exerciseId: exercise._id,
            time: formatTime(durationInSeconds),
            comparison: comparison,
            comparisonClass: comparisonClass, // Klasse für die Farbe
            icon: getImage(),
          }
        })
        .filter(Boolean)
    },
    totalDistance() {
      return Object.values(this.completedSetsMap)
        .flat()
        .reduce((acc, set) => {
          if (set.skipped) return acc
          const d = set.find((i) => i.text === "Distanz")?.value || 0
          return acc + d
        }, 0)
    },

    // ... (alle anderen computed properties wie `planUpdates`, `cardStyle` etc. bleiben unverändert)
    planUpdates() {
      const updates = []
      if (!this.activeSplit || !this.originalActiveSplit) return []
      const finalUpdatedSplit = this.getUpdatedSplit([])
      finalUpdatedSplit.exercises.forEach((currentExercise) => {
        const originalExercise = this.originalActiveSplit.exercises.find((e) => e._id === currentExercise._id)
        if (!originalExercise) return
        const processExerciseSets = (currentEx, originalEx) => {
          if (!currentEx.sets || !originalEx.sets) return
          for (let i = 0; i < currentEx.sets.length; i++) {
            const newWeight = currentEx.sets[i]?.find((s) => s.text === "Gewicht")?.value
            const oldWeight = originalEx.sets[i]?.find((s) => s.text === "Gewicht")?.value
            if (newWeight != null && oldWeight != null && newWeight !== oldWeight) {
              updates.push({
                exerciseTitle: currentEx.isSuperset ? `${currentEx.title} (Supersatz)` : currentEx.title,
                oldValue: `${oldWeight} kg`,
                newValue: `${newWeight} kg`,
                isIncrease: newWeight > oldWeight,
              })
              return
            }
          }
        }
        if (currentExercise.isSuperset) {
          currentExercise.exercises.forEach((currentSubEx) => {
            const originalSubEx = originalExercise.exercises?.find((e) => e._id === currentSubEx._id)
            if (originalSubEx) {
              processExerciseSets(currentSubEx, originalSubEx)
            }
          })
        } else {
          processExerciseSets(currentExercise, originalExercise)
        }
      })
      const maxWeightRecordTitles = this.newPersonalRecords.filter((r) => r.type === "Maximalgewicht").map((r) => r.exerciseTitle)
      return updates.filter((update) => !maxWeightRecordTitles.includes(update.exerciseTitle))
    },
    cardStyle() {
      return {
        borderColor: `${this.splitColor}55 !important`,
        boxShadow: `0 0 25px ${this.splitColor}69`,
      }
    },
    personalizedMessage() {
      if (this.newPersonalRecords.length > 1) {
        return `Wahnsinns-Leistung! Du hast heute ${this.newPersonalRecords.length} neue Rekorde aufgestellt.`
      }
      if (this.newPersonalRecords.length === 1) {
        return `Stark! Neuer persönlicher Rekord im ${this.newPersonalRecords[0].type}. Weiter so!`
      }
      if (this.totalSets > 5) {
        return "Konstanz ist der Schlüssel. Eine solide Einheit abgeschlossen!"
      }
      return "Gute Arbeit! Jedes Training zählt für dein Ziel."
    },
    splitTitle() {
      return this.activeSplit?.title || "Training"
    },
    splitColor() {
      return this.activeSplit?.fixedVisualization?.color || "#333"
    },
    splitImage() {
      return this.activeSplit?.fixedVisualization?.img || ""
    },
    heroStyle() {
      return { background: `radial-gradient(circle, ${this.splitColor}33 0%, rgba(30,30,30,0) 70%)` }
    },
    durationInSeconds() {
      if (!this.workoutStartTime) return 0
      return moment().diff(moment(this.workoutStartTime), "seconds")
    },
    durationString() {
      const s = this.durationInSeconds
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const p = (n) => String(n).padStart(2, "0")
      return h > 0 ? `${p(h)}:${p(m)}:${p(s % 60)}` : `${p(m)}:${p(s % 60)}`
    },
    totalVolume() {
      return Object.values(this.completedSetsMap)
        .flat()
        .reduce((acc, set) => {
          if (set.skipped) return acc
          const w = set.find((i) => i.text === "Gewicht")?.value || 0
          const r = set.find((i) => /Wiederholungen/.test(i.text))?.value || 0
          return acc + w * r
        }, 0)
    },
    totalSets() {
      return Object.values(this.completedSetsMap)
        .flat()
        .filter((s) => !s.skipped).length
    },
    totalReps() {
      return Object.values(this.completedSetsMap)
        .flat()
        .reduce((acc, set) => {
          if (set.skipped) return acc
          const r = set.find((i) => /Wiederholungen/.test(i.text))?.value || 0
          return acc + r
        }, 0)
    },
    caloriesBurned() {
      return (this.durationInSeconds / 60) * 7
    },
    focusTags() {
      if (!this.activeSplit?.exercises) return []
      const allTags = this.activeSplit.exercises.flatMap((ex) => ex.tags.map((tag) => tag.name))
      return [...new Set(allTags)]
    },
    planAdjustments() {
      if (!this.activeSplit || !this.originalActiveSplit) return []
      const adjustments = []
      this.activeSplit.exercises.forEach((currentExercise, index) => {
        const originalExercise = this.originalActiveSplit.exercises.find((ex) => ex._id === currentExercise._id)
        if (!originalExercise || !originalExercise.sets || !currentExercise.sets) return
        const firstOriginalSetWithWeight = originalExercise.sets.find((set) => set.some((s) => s.text === "Gewicht"))
        const firstCurrentSetWithWeight = currentExercise.sets.find((set) => set.some((s) => s.text === "Gewicht"))
        if (firstOriginalSetWithWeight && firstCurrentSetWithWeight) {
          const oldWeight = firstOriginalSetWithWeight.find((s) => s.text === "Gewicht").value
          const newWeight = firstCurrentSetWithWeight.find((s) => s.text === "Gewicht").value
          if (oldWeight !== newWeight) {
            const isAlreadyRecord = this.newPersonalRecords.some(
              (pr) => pr.exerciseTitle === currentExercise.title && pr.newValue.includes(newWeight)
            )
            if (!isAlreadyRecord) {
              adjustments.push({
                exerciseTitle: currentExercise.title,
                oldValue: `${oldWeight} kg`,
                newValue: `${newWeight} kg`,
                isIncrease: newWeight > oldWeight,
              })
            }
          }
        }
      })
      return adjustments
    },
    newPersonalRecords() {
      if (!this.hubGroupedExercises || !this.completedSetsMap || !this.activeSplit) return []
      const records = []
      Object.entries(this.completedSetsMap).forEach(([exerciseIndex, sets]) => {
        if (!sets || sets.length === 0) return
        const exerciseDef = this.activeSplit.exercises[exerciseIndex]
        if (!exerciseDef) return
        const processExercise = (subExercise, subSets) => {
          const historicalData = this.hubGroupedExercises.find((ex) => ex.exercise._id === subExercise._id)
          if (!historicalData) return
          let currentMaxWeight = 0
          let currentMaxVolume = 0
          subSets.forEach((set) => {
            if (set.skipped) return
            const weight = set.find((item) => item.text === "Gewicht")?.value || 0
            const reps = set.find((item) => /Wiederholungen/.test(item.text))?.value || 0
            if (weight > currentMaxWeight) currentMaxWeight = weight
            if (weight * reps > currentMaxVolume) currentMaxVolume = weight * reps
          })
          const oldMaxWeight = historicalData.summary?.maxWeightStep?.weight || 0
          const oldMaxVolume = (historicalData.summary?.maxVolumeStep?.weight || 0) * (historicalData.summary?.maxVolumeStep?.reps || 0)
          if (currentMaxWeight > oldMaxWeight) {
            records.push({
              exerciseTitle: subExercise.title,
              type: "Maximalgewicht",
              oldValue: `${oldMaxWeight} kg`,
              newValue: `${currentMaxWeight} kg`,
            })
          }
          if (currentMaxVolume > oldMaxVolume) {
            records.push({
              exerciseTitle: subExercise.title,
              type: "Maximalvolumen",
              oldValue: `${oldMaxVolume.toFixed(0)} kg`,
              newValue: `${currentMaxVolume.toFixed(0)} kg`,
            })
          }
        }
        if (exerciseDef.isSuperset) {
          const setsBySubExercise = sets.reduce((acc, set) => {
            if (set.subExerciseId) {
              if (!acc[set.subExerciseId]) acc[set.subExerciseId] = []
              acc[set.subExerciseId].push(set)
            }
            return acc
          }, {})
          Object.entries(setsBySubExercise).forEach(([subExerciseId, subSets]) => {
            const subExerciseDef = exerciseDef.exercises.find((ex) => ex._id === subExerciseId)
            if (subExerciseDef) {
              processExercise(subExerciseDef, subSets)
            }
          })
        } else {
          processExercise(exerciseDef, sets)
        }
      })
      return records
    },
  },
  methods: {
    ...mapActions("runWorkoutStore", ["resetWorkoutState"]),
    toggleSegment(index) {
      // Klappt das Segment auf oder zu
      this.expandedSegmentIndex = this.expandedSegmentIndex === index ? null : index
    },

    getSegmentHistory(segment) {
      if (!this.hubGroupedSessions || !segment || !this.activeSplit) return []

      const history = []

      // 1. Finde ALLE vergangenen Sessions mit exakt demselben Titel
      // Wir filtern die Sessions aus dem Vuex Store
      const pastRaces = this.hubGroupedSessions.filter(
        (session) =>
          session.execution &&
          session.execution.title === this.activeSplit.title &&
          session.execution.hyroxSegments &&
          session.execution.hyroxSegments.length > 0
      )

      // 2. Durchlaufe diese Rennen und extrahiere die Leistung für das spezifische Segment
      pastRaces.forEach((race) => {
        // Finde das passende Segment anhand des Index (z.B. immer der 1. Lauf)
        const pastSegment = race.execution.hyroxSegments.find((s) => s.segmentIndex === segment.segmentIndex)

        if (pastSegment && pastSegment.duration > 0) {
          const duration = Number(pastSegment.duration)
          const minutes = Math.floor(duration / 60)
          const seconds = Math.floor(duration % 60)

          history.push({
            // Wir nehmen den Zeitstempel der Execution für das Datum
            date: moment(parseInt(race.execution.ts, 10) * 1000).format("DD.MM.YYYY"),
            time: `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`,
          })
        }
      })

      // 3. Füge die HEUTIGE Leistung hinzu, damit sie in der Liste erscheint
      const currentTime = segment.time
      if (currentTime !== "---") {
        // unshift fügt das Element an den Anfang der Liste
        history.unshift({
          date: "Heute",
          time: currentTime,
        })
      }

      // Die Liste ist bereits durch das Backend vorsortiert (neueste zuerst),
      // und wir fügen "Heute" an den Anfang. Perfekt!
      return history
    },
    getImageForExercise(ex) {
      if (ex.picture) return this.getImageUrl(ex.picture)
      if (ex.manual?.[0]?.picture) return this.getImageUrl(ex.manual[0].picture)

      return "/img/default-exercise.webp"
    },
    getImageUrl(id) {
      if (!id) return "/img/default-exercise.webp"
      let fn = String(id)
      if (fn.includes("/")) {
        fn = fn.substring(fn.lastIndexOf("/") + 1)
      }
      const nWoE = fn.replace(/\.[^/.]+$/, "")
      if (!nWoE) return "/img/default-exercise.webp"
      return `/img/uebungen/${id.replace(/\.[^/.]+$/, "")}.webp`
    },
    // --- START: KOMPLETT NEUE SPEICHERLOGIK ---
    async saveSessionAndExit() {
      if (this.isSaving) return

      if (this.activeSplit.isFreeTraining) {
        this.showSaveFreeTrainingDialog = true
      } else {
        await this.executeSave({ shouldSaveAsSplit: false, planId: this.activePlanID })
      }
    },

    async handleSaveFreeTraining(saveAsSplit) {
      await this.executeSave({ shouldSaveAsSplit: saveAsSplit, planId: this.activePlanID })
      this.showSaveFreeTrainingDialog = false
    },

    async executeSave({ shouldSaveAsSplit, planId }) {
      this.isSaving = true
      let finalPlanId = planId

      if (shouldSaveAsSplit) {
        try {
          const splitToSave = { ...this.activeSplit }
          delete splitToSave.isFreeTraining
          const response = await api.workoutApi.addSplit({
            workout_id: finalPlanId, // Ist null, wenn kein Plan existiert
            split: splitToSave,
          })

          if (response && response && response._id) {
            console.log("Freies Training erfolgreich als Split gespeichert:", response._id, "response:", response)
            finalPlanId = response._id // ID des neuen/aktualisierten Plans erhalten
            this.$toast.success("Dein Training wurde als neuer Plan gespeichert!")
          } else {
            throw new Error("Ungültige Antwort vom Server beim Speichern des Splits.")
          }
        } catch (error) {
          console.error("Fehler beim Speichern des freien Trainings als Split:", error)
          this.$toast.error("Der Plan konnte nicht gespeichert werden. Deine Statistik wird aber trotzdem erfasst.")
        }
      }
      console.log("isch abassssst?", finalPlanId, this.activeSplit.isFreeTraining)
      try {
        if (window.flutter_inappwebview) {
          await window.flutter_inappwebview.callHandler("workoutActivityHandler", { status: "stop" })
        }

        this.unixTimestamp = moment().unix()

        const isHyrox = this.activeSplit.isHyrox

        const executionData = {
          title: this.activeSplit.title,
          fixedVisualization: this.activeSplit.fixedVisualization,
          workout_id: finalPlanId,
          duration: this.durationInSeconds,
          records: this.newPersonalRecords,
          liftedWeight: this.totalVolume,
          totalDistance: this.totalDistance,
          caloriesBurned: this.caloriesBurned.toFixed(0),
          feedBack: this.trainingFeedback,
          ts: this.unixTimestamp,
        }

        let legacySets = []

        // Wenn es ein Hyrox-Rennen ist, erstellen wir die neue, strukturierte Segment-Liste
        if (isHyrox) {
          executionData.hyroxSegments = this.activeSplit.exercises.map((exercise, index) => {
            const completedSet = (this.completedSetsMap[index] || [])[0]

            // Extrahiere ALLE relevanten Daten aus dem gespeicherten Satz
            const duration = completedSet ? Number(completedSet.find((s) => s.text === "Dauer")?.value || 0) : 0
            const weight = completedSet ? Number(completedSet.find((s) => s.text === "Gewicht")?.value || 0) : 0
            const reps = completedSet ? Number(completedSet.find((s) => /Wiederholungen/.test(s.text))?.value || 0) : 0
            const distance = completedSet ? Number(completedSet.find((s) => s.text === "Distanz")?.value || 0) : 0

            const originalTitle = this.originalActiveSplit.exercises[index]?.title || exercise.title

            // Erstelle das vollständige Datenpaket für das Backend
            return {
              segmentIndex: index,
              title: originalTitle,
              duration: duration,
              exercise_id: exercise._id,
              weight: weight,
              reps: reps,
              distance: distance,
            }
          })
        }
        // Ansonsten verwenden wir die alte Methode
        else {
          legacySets = this.transformSetsForBackend(finalPlanId)
        }

        await api.workoutApi.saveWorkoutSession({ execution: executionData, sets: legacySets })

        if (!this.activeSplit.isFreeTraining && this.didWeightsChange()) {
          const splitWithUpdatedWeights = this.getUpdatedSplit([])
          await api.workoutApi.updateSplit({ workout_id: finalPlanId, split: splitWithUpdatedWeights })
        }

        this.resetWorkoutState()
        this.$router.push("/workout/hub")
      } catch (error) {
        console.error("Fehler beim Speichern der Session-Statistik:", error)
        alert("Es gab einen Fehler beim Speichern deiner Session-Statistiken.")
      } finally {
        this.isSaving = false
      }
    },
    // --- ENDE: KOMPLETT NEUE SPEICHERLOGIK ---

    handleSaveChanges(modificationsToApply) {
      this.saveChangesDialogVisible = false
      if (modificationsToApply.length > 0) {
        this.updatePlanInBackend(modificationsToApply)
      }
    },
    handleDiscardChanges() {
      this.saveChangesDialogVisible = false
      this.$toast.info("Änderungen am Plan wurden verworfen.")
    },
    async updatePlanInBackend(modificationsToApply) {
      const updatedSplit = this.getUpdatedSplit(modificationsToApply)
      try {
        await api.workoutApi.updateSplit({ workout_id: this.activePlanID, split: updatedSplit })
        this.$toast.success("Dein Trainingsplan wurde erfolgreich aktualisiert!")
      } catch (error) {
        console.error("Fehler beim Aktualisieren des Plans:", error)
        this.$toast.error("Plan konnte nicht aktualisiert werden.")
      }
    },
    getUpdatedSplit(modificationsToApply) {
      const splitCopy = JSON.parse(JSON.stringify(this.originalActiveSplit))
      if (modificationsToApply && modificationsToApply.length > 0) {
        modificationsToApply.forEach((mod) => {
          if (mod.type === "swap") {
            const targetIndex = splitCopy.exercises.findIndex((ex) => ex._id === mod.payload.originalExercise._id)
            if (targetIndex > -1) {
              splitCopy.exercises[targetIndex] = mod.payload.newExercise
            }
          }
        })
        const additions = modificationsToApply.filter((mod) => mod.type === "add").sort((a, b) => a.payload.atIndex - b.payload.atIndex)
        additions.forEach((mod) => {
          splitCopy.exercises.splice(mod.payload.atIndex, 0, mod.payload.newExercise)
        })
      }
      Object.entries(this.completedSetsMap).forEach(([exerciseIndex, performedSets]) => {
        const performedExerciseId = this.activeSplit.exercises[exerciseIndex]._id
        const exerciseToUpdateInCopy = splitCopy.exercises.find((ex) => ex._id === performedExerciseId)
        const originalExercise = this.originalActiveSplit.exercises.find((ex) => ex._id === performedExerciseId)
        if (!exerciseToUpdateInCopy || !originalExercise || !exerciseToUpdateInCopy.sets || !originalExercise.sets) {
          return
        }
        const hasWarmup = originalExercise.warmup && originalExercise.warmup.length > 0
        performedSets.forEach((performedSet, performedSetIndex) => {
          if (hasWarmup && performedSetIndex === 0) {
            return
          }
          const regularSetIndex = hasWarmup ? performedSetIndex - 1 : performedSetIndex
          const newWeightSuggestion = performedSet.find((item) => item.text === "newWeight")?.value
          const actualLiftedWeight = performedSet.find((item) => item.text === "Gewicht")?.value
          const originalSetDef = originalExercise.sets[regularSetIndex]
          if (!originalSetDef) return
          const plannedWeight = originalSetDef.find((s) => s.text === "Gewicht")?.value
          let finalWeightToSet = null
          if (newWeightSuggestion != null) {
            finalWeightToSet = newWeightSuggestion
          } else if (actualLiftedWeight != null && plannedWeight != null && actualLiftedWeight !== plannedWeight) {
            finalWeightToSet = actualLiftedWeight
          }
          if (finalWeightToSet !== null) {
            const setToUpdateInPlan = exerciseToUpdateInCopy.sets[regularSetIndex]
            if (setToUpdateInPlan) {
              const weightSettingInPlan = setToUpdateInPlan.find((s) => s.text === "Gewicht")
              if (weightSettingInPlan) {
                weightSettingInPlan.value = finalWeightToSet
              }
            }
          }
        })
      })
      return splitCopy
    },
    didWeightsChange() {
      const finalUpdatedSplit = this.getUpdatedSplit([])
      return finalUpdatedSplit.exercises.some((updatedExercise) => {
        const originalExercise = this.originalActiveSplit.exercises.find((e) => e._id === updatedExercise._id)
        if (!originalExercise) return false
        if (updatedExercise.isSuperset) {
          return updatedExercise.exercises.some((updatedSubEx) => {
            const originalSubEx = originalExercise.exercises?.find((e) => e._id === updatedSubEx._id)
            if (!originalSubEx || !updatedSubEx.sets || !originalSubEx.sets) return false
            return updatedSubEx.sets.some((updatedSet, setIndex) => {
              const originalSet = originalSubEx.sets[setIndex]
              if (!originalSet) return false
              const newWeight = updatedSet.find((s) => s.text === "Gewicht")?.value
              const oldWeight = originalSet.find((s) => s.text === "Gewicht")?.value
              return newWeight != null && oldWeight != null && newWeight !== oldWeight
            })
          })
        } else {
          if (!updatedExercise.sets || !originalExercise.sets) return false
          return updatedExercise.sets.some((updatedSet, setIndex) => {
            const originalSet = originalExercise.sets[setIndex]
            if (!originalSet) return false
            const newWeight = updatedSet.find((s) => s.text === "Gewicht")?.value
            const oldWeight = originalSet.find((s) => s.text === "Gewicht")?.value
            return newWeight != null && oldWeight != null && newWeight !== oldWeight
          })
        }
      })
    },
    transformSetsForBackend(freeTrainingWorkoutID = null) {
      const exerciseExecutions = []
      Object.entries(this.completedSetsMap).forEach(([exerciseIndex, performedSets]) => {
        if (!performedSets || performedSets.length === 0 || performedSets.every((s) => s.skipped)) {
          return
        }
        const exercise = this.activeSplit.exercises[exerciseIndex]
        if (!exercise) return
        const steps = performedSets
          .map((set, setIndex) => {
            if (set.skipped) return null
            const weight = set.find((item) => item.text === "Gewicht")?.value || 0
            const reps = set.find((item) => /Wiederholungen/.test(item.text))?.value || 0
            const distance = set.find((item) => item.text === "Distanz")?.value || 0 // NEU
            return {
              set: setIndex + 1,
              weight: weight,
              reps: reps,
              distance: distance, // NEU
              time: set.find((item) => item.text === "Dauer")?.value || null,
              feedback: {
                difficulty: set.find((item) => item.text === "Difficulty")?.value,
                newWeightSuggestion: set.find((item) => item.text === "newWeight")?.value,
              },
            }
          })
          .filter(Boolean)
        if (steps.length > 0) {
          exerciseExecutions.push({
            exercise_id: exercise._id,
            split: this.activeSplit.title,
            workoutId: freeTrainingWorkoutID || this.activePlanID,
            timestamp: this.unixTimestamp,
            steps: steps,
          })
        }
      })
      return exerciseExecutions
    },
    animateStats() {
      this.animateValue("animatedVolume", this.totalVolume, 1000)
      this.animateValue("animatedDistance", this.totalDistance, 1100)
      this.animateValue("animatedSets", this.totalSets, 1200)
      this.animateValue("animatedReps", this.totalReps, 1300)
      this.animateValue("animatedCalories", this.caloriesBurned, 1400)
    },
    animateValue(refName, endValue, duration) {
      let startTimestamp = null
      const startValue = 0
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp
        const progress = Math.min((timestamp - startTimestamp) / duration, 1)
        this[refName] = Math.floor(progress * (endValue - startValue) + startValue)
        if (progress < 1) {
          window.requestAnimationFrame(step)
        }
      }
      window.requestAnimationFrame(step)
    },
    triggerConfetti() {
      const duration = 2 * 1000
      const animationEnd = Date.now() + duration
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 }
      function randomInRange(min, max) {
        return Math.random() * (max - min) + min
      }
      const interval = setInterval(function () {
        const timeLeft = animationEnd - Date.now()
        if (timeLeft <= 0) {
          return clearInterval(interval)
        }
        const particleCount = 50 * (timeLeft / duration)
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }))
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }))
      }, 250)
    },
  },
  mounted() {
    this.animateStats()
    if (this.getWorkoutModifications.length > 0 && !this.activeSplit.isFreeTraining) {
      setTimeout(() => {
        this.saveChangesDialogVisible = true
      }, 1500)
    } else {
      this.triggerConfetti()
    }
  },
}
</script>
<style scoped>
/* GRUNDLAGEN & HINTERGRUND-ANIMATION */
.summary-page {
  color: white;
  padding-bottom: 120px;
  animation: subtle-gradient-pan 15s ease infinite;
}

@keyframes subtle-gradient-pan {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/* HERO-BEREICH & TITEL-EFFEKT */
.hero-section {
  padding: 24px 0;
}

.hero-title {
  /* Dynamischer Farbverlauf für den Text, der die Split-Farbe nutzt */
  background: linear-gradient(45deg, #fff, v-bind(splitColor));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  /* Neon-Leuchteffekt */
  text-shadow: 0 0 5px rgba(255, 255, 255, 0.5), 0 0 15px v-bind(splitColor);
  animation: text-shimmer 3s infinite;
  background-size: 200% auto;
}

@keyframes text-shimmer {
  0% {
    background-position: 200% center;
  }
  100% {
    background-position: -200% center;
  }
}

/* VERBESSERTE PULS-ANIMATION für den Helden */
.pulsing-hero {
  animation: pulse-glow-enhanced 4s infinite ease-in-out;
}

@keyframes pulse-glow-enhanced {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.03);
  }
}

/* NEUE REKORDE-KARTEN (PR-CARDS) - DER "EPIC-LOOT"-LOOK */
.pr-card {
  border-radius: 16px;
  padding: 16px;
  width: 240px;
  text-align: center;
  border: 2px solid transparent;
  background: linear-gradient(135deg, #2a2a2a, #1e1e1e) padding-box, linear-gradient(135deg, v-bind(splitColor), #444) border-box;
  transition: all 0.3s ease;
  animation: card-appear 0.5s backwards;
}

.pr-card:hover {
  transform: translateY(-5px) scale(1.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.pr-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: white;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.pr-type {
  font-size: 0.8rem;
  color: #bdbdbd;
}

.pr-values {
  margin-top: 8px;
  font-size: 1.2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
}

.old-value {
  color: #9e9e9e;
  text-decoration: line-through;
}

.new-value {
  font-weight: bold;
  color: #a5d6a7; /* Grün für Erfolg */
  text-shadow: 0 0 8px #a5d6a7;
}

/* STATISTIK-KARTEN - Moderner & Interaktiver */
.stat-card {
  background-color: rgba(30, 30, 30, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  height: 100%;
  transition: all 0.3s ease-in-out;
  animation: card-appear 0.5s backwards;
}

/* Gestaffelte Animation für die Statistik-Karten */
.v-col:nth-child(2) .stat-card {
  animation-delay: 0.1s;
}
.v-col:nth-child(3) .stat-card {
  animation-delay: 0.2s;
}
.v-col:nth-child(4) .stat-card {
  animation-delay: 0.3s;
}
.v-col:nth-child(5) .stat-card {
  animation-delay: 0.4s;
}

@keyframes card-appear {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.stat-card:hover {
  transform: scale(1.05);
  border-color: v-bind(splitColor);
  box-shadow: 0 0 30px v-bind(splitColor) 33;
}

.stat-emoji {
  font-size: 2.5rem;
  line-height: 1;
  margin-bottom: 8px;
  transform: scale(1);
  transition: transform 0.2s ease;
}

.stat-card:hover .stat-emoji {
  transform: scale(1.2) rotate(-5deg);
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: white;
}

.stat-label {
  font-size: 0.8rem;
  color: #bdbdbd;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* FOOTER */
.footer-bg {
  background: linear-gradient(to top, rgba(20, 20, 20, 1) 50%, rgba(20, 20, 20, 0));
}

.motivational-quote {
  font-style: italic;
  color: #ccc;
}

.feedback-textarea.v-text-field--filled > .v-input__control > .v-input__slot {
  background: rgba(30, 30, 30, 0.7) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: border-color 0.3s ease;
}

.feedback-textarea.v-text-field--filled.v-input--is-focused > .v-input__control > .v-input__slot {
  border-color: v-bind(splitColor) !important;
}

.summary-card {
  border-radius: 16px;
  padding: 16px;
  width: 240px;
  text-align: center;
  border: 2px solid transparent;
  background: linear-gradient(135deg, #2a2a2a, #1e1e1e) padding-box, linear-gradient(135deg, v-bind(splitColor), #444) border-box;
  transition: all 0.3s ease;
  animation: card-appear 0.5s backwards;
}

.summary-card:hover {
  transform: translateY(-5px) scale(1.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.summary-card-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: white;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.summary-card-type {
  font-size: 0.8rem;
  color: #bdbdbd;
}

.summary-card-values {
  margin-top: 8px;
  font-size: 1.2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
}

.old-value {
  color: #9e9e9e;
  text-decoration: line-through;
}

/* SPEZIFISCHE STYLES FÜR REKORD-KARTEN */
.pr-card .new-value {
  font-weight: bold;
  color: #a5d6a7; /* Grün für Erfolg */
  text-shadow: 0 0 8px #a5d6a7;
}

/* NEUE SPEZIFISCHE STYLES FÜR ANPASSUNGS-KARTEN */
.adj-card {
  background: linear-gradient(135deg, #2a2a2a, #1e1e1e) padding-box, linear-gradient(135deg, #42a5f5, #1e88e5) border-box; /* Blauer Rand */
}

.adj-card .new-value {
  font-weight: bold;
}

.adj-card .new-value.increase {
  color: #a5d6a7; /* Grün für Erhöhung */
}

.adj-card .new-value.decrease {
  color: #ffcc80; /* Orange für Reduzierung */
}

.hyrox-summary {
  color: white;
  padding-bottom: 120px;
  background: #0a0a0a;
  position: relative;
  overflow: hidden;
}

.hyrox-summary-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 0%, rgba(255, 237, 0, 0.15), transparent 60%);
  opacity: 0;
  animation: aurora-glow 1s 0.5s forwards;
}

.hyrox-hero {
  padding: 48px 0 24px 0;
}

.hyrox-title {
  font-family: "Teko", sans-serif;
  font-size: 4rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #ffed00;
  text-shadow: 0 0 20px #ffed00;
  line-height: 1;
}

.total-time-display {
  font-family: monospace;
  font-size: 4rem;
  font-weight: bold;
  color: white;
  margin: 16px 0;
}

.hyrox-subtitle {
  font-size: 1.2rem;
  color: #aaa;
}

.segment-list .segment-item {
  background: rgba(255, 255, 255, 0.04);
  border-radius: 16px;
  margin-bottom: 4px;
  padding: 12px 16px !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}
.segment-list .segment-item:hover {
  border-color: #ffed00;
  background: rgba(255, 237, 0, 0.08);
}

.segment-title {
  font-weight: bold;
  font-size: 1rem;
}

.segment-time {
  font-family: monospace;
  font-size: 1.2rem;
  font-weight: bold;
  color: white;
}

.segment-comparison {
  font-family: monospace;
  font-size: 0.9rem;
  font-weight: bold;
}
.segment-comparison.faster {
  color: #4caf50;
}
.segment-comparison.slower {
  color: #f44336;
}

.rotate-icon {
  transform: rotate(180deg);
}
.v-list-item__action .v-icon {
  transition: transform 0.3s ease-in-out;
}

.segment-history-wrapper {
  padding: 0 16px 16px 16px;
}

.segment-history-table {
  background-color: rgba(0, 0, 0, 0.2) !important;
  border-radius: 12px;
}
.segment-history-table th,
.segment-history-table td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

@keyframes aurora-glow {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
